// Frozen copy of main's code for the document_generation_v4_enabled / agency_brand_design_enabled flag-off path; delete with the flag.
import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  StrikethroughIcon,
  CodeBracketIcon,
  ListBulletIcon,
  NumberedListIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  PaintBrushIcon,
  Bars3BottomLeftIcon,
  Bars3BottomRightIcon,
  NoSymbolIcon
} from '@heroicons/react/20/solid';
import { PhotoIcon, EyeIcon } from '@heroicons/react/24/outline';
import type { Editor as EditorType } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import { EditorLinkButton } from '~/components/document-editor/editor-link-button';

// Custom center-align icon matching the style of bars-3-bottom-left
const Bars3CenterIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 6.75h16.5M7.875 12h8.25M3.75 17.25h16.5"
    />
  </svg>
);

// Custom fill bucket icon matching the style of paint brush
const FillBucketIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1}
    stroke="currentColor"
    className={className}
  >
    <path d="M20.911 14.216l-.411-.596-.411.596C19.74 14.72 18 17.3 18 18.5a2.5 2.5 0 0 0 5 0c0-1.2-1.74-3.78-2.089-4.284zM20.5 20a1.502 1.502 0 0 1-1.5-1.5 9.725 9.725 0 0 1 1.5-3.096A9.725 9.725 0 0 1 22 18.5a1.502 1.502 0 0 1-1.5 1.5zm-9-17.207L9.145 5.148a.476.476 0 0 0-.09-.023c-3.475-.17-5.962.425-6.743 1.59-.027.042-.07.077-.092.12a1.394 1.394 0 0 0 .118 1.522c.694.973 2.685 1.732 5.833 1.732a23.887 23.887 0 0 0 2.89-.192 1.494 1.494 0 1 0 .076-1.016c-4.77.618-7.418-.308-7.986-1.104-.812-1.14 3.1-1.71 5.044-1.679L6.32 7.973c.386.05.836.08 1.318.096L11.5 4.207l7.293 7.293-8.09 8.091a1.74 1.74 0 0 1-2.405 0l-4.889-4.888a1.702 1.702 0 0 1 0-2.405l1.514-1.514a9.152 9.152 0 0 1-1.101-.312l-1.12 1.12a2.703 2.703 0 0 0 0 3.818l4.889 4.888a2.7 2.7 0 0 0 3.818 0l8.798-8.798zM12 9.5a.5.5 0 1 1 .5.5.5.5 0 0 1-.5-.5z" />
  </svg>
);

// Font size options for the font size selector
const FONT_SIZES = [
  '8',
  '9',
  '10',
  '11',
  '12',
  '14',
  '16',
  '18',
  '20',
  '24',
  '28',
  '32',
  '36',
  '48',
  '72'
] as const;

const ColorPickerButton = ({
  icon,
  text,
  color,
  onChange,
  disabled,
  title
}: {
  icon?: React.ReactNode;
  text?: string;
  color: string;
  onChange: (color: string) => void;
  disabled: boolean;
  title: string;
}) => {
  return (
    <label
      className={clsx(
        'relative p-1 rounded-md cursor-pointer flex flex-col items-center gap-[1px] w-6',
        disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-neutral-200 dark:hover:bg-white/10'
      )}
      title={title}
    >
      {/* Icon or Text */}
      {text && (
        <span className="text-xs/3 font-bold text-neutral-700 dark:text-neutral-300">{text}</span>
      )}
      {icon && <div className="text-neutral-700 dark:text-neutral-300">{icon}</div>}

      {/* Color Indicator Bar */}
      <div className="w-full h-0.5 rounded-[1px]" style={{ backgroundColor: color }} />

      {/* Hidden Color Input */}
      <input
        type="color"
        value={color}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="absolute opacity-0 w-0 h-0"
      />
    </label>
  );
};

const FontSizeSelector = ({
  currentFontSize,
  onSetFontSize,
  onUnsetFontSize,
  disabled
}: {
  currentFontSize: string | null;
  onSetFontSize: (size: string) => void;
  onUnsetFontSize: () => void;
  disabled: boolean;
}) => {
  return (
    <Headless.Menu as="div" className="relative z-30">
      <Headless.MenuButton
        as={Headless.Button}
        disabled={disabled}
        className={clsx(
          'px-2 py-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md text-xs font-medium min-w-[3.5rem] text-center',
          disabled
            ? 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
            : currentFontSize
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
        )}
      >
        {currentFontSize ? currentFontSize.replace('px', '') : 'Size'}
      </Headless.MenuButton>
      <Headless.MenuItems className="absolute left-0 top-full mt-1 w-24 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md shadow-lg z-30 max-h-60 overflow-y-auto">
        {FONT_SIZES.map((size) => {
          const sizeValue = `${size}px`;
          const isActive = currentFontSize === sizeValue;
          return (
            <Headless.MenuItem key={size}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => onSetFontSize(sizeValue)}
                  className={clsx(
                    'w-full px-3 py-1.5 text-left text-sm',
                    active && 'bg-neutral-100 dark:bg-neutral-700',
                    isActive && 'text-primary-500 dark:text-primary-400 font-semibold',
                    !isActive && 'text-neutral-700 dark:text-neutral-300'
                  )}
                >
                  {sizeValue}
                </button>
              )}
            </Headless.MenuItem>
          );
        })}
        <div className="border-t border-neutral-200 dark:border-neutral-700">
          <Headless.MenuItem>
            {({ active }) => (
              <button
                type="button"
                onClick={onUnsetFontSize}
                className={clsx(
                  'w-full px-3 py-1.5 text-left text-sm',
                  active && 'bg-neutral-100 dark:bg-neutral-700',
                  !currentFontSize
                    ? 'text-primary-500 dark:text-primary-400 font-semibold'
                    : 'text-neutral-700 dark:text-neutral-300'
                )}
              >
                Default
              </button>
            )}
          </Headless.MenuItem>
        </div>
      </Headless.MenuItems>
    </Headless.Menu>
  );
};

// The toolbar state for an editor that can no longer answer questions about
// itself: everything unavailable, colors at their defaults.
const DISABLED_EDITOR_STATE = {
  hasSelection: false,
  canSetTextColor: false,
  canSetHighlight: false,
  currentTextColor: '#000000',
  currentHighlightColor: '#ffffff',
  currentFontSize: null as string | null,
  canSetFontSize: false,
  isBold: false,
  canBold: false,
  isItalic: false,
  canItalic: false,
  isStrike: false,
  isUnderline: false,
  canUnderline: false,
  canStrike: false,
  isAlignLeft: false,
  isAlignCenter: false,
  isAlignRight: false,
  canSetTextAlign: false,
  isCode: false,
  canCode: false,
  canClearMarks: false,
  isLink: false,
  canSetLink: false,
  isParagraph: false,
  isHeading1: false,
  isHeading2: false,
  isHeading3: false,
  isBulletList: false,
  isOrderedList: false,
  isCodeBlock: false,
  isBlockquote: false,
  canUndo: false,
  canRedo: false,
  canInsertPageBreak: false,
  canInsertImagePlaceholder: false,
  isInTable: false,
  canAddRowBefore: false,
  canAddRowAfter: false,
  canAddColumnBefore: false,
  canAddColumnAfter: false,
  canDeleteRow: false,
  canDeleteColumn: false
};

export const MenuBar = ({
  editor,
  showPreview,
  onPreviewToggle,
  showImageButton = true,
  subtle
}: {
  editor: EditorType;
  showPreview?: boolean;
  onPreviewToggle?: () => void;
  showImageButton?: boolean;
  /**
   * Trades the filled tray for a hairline strip. A document editor is a page
   * with a toolbar over it; a mail composer is a message with formatting
   * available, and a grey bar between the header and the body says the wrong
   * one of those.
   */
  subtle?: boolean;
}) => {
  // Read the current editor's state, and re-render the component when it changes
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      // can() throws on a destroyed editor, so degrade to a disabled toolbar
      // instead of crashing.
      if (ctx.editor.isDestroyed) {
        return DISABLED_EDITOR_STATE;
      }
      return {
        hasSelection: !ctx.editor.state.selection.empty,
        canSetTextColor: ctx.editor.can().chain().setColor('#000000').run() ?? false,
        canSetHighlight: ctx.editor.can().chain().setBackgroundColor('#ffffff').run() ?? false,
        currentTextColor:
          (ctx.editor.getAttributes('textStyle').color as string | undefined) ?? '#000000',
        currentHighlightColor: ctx.editor.storage.smartHighlight.getCurrentColor(ctx.editor),
        currentFontSize:
          (ctx.editor.getAttributes('fontSize').fontSize as string | undefined) ?? null,
        canSetFontSize: ctx.editor.can().chain().setFontSize('12px').run() ?? false,
        isBold: ctx.editor.isActive('bold') ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive('italic') ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isStrike: ctx.editor.isActive('strike') ?? false,
        isUnderline: ctx.editor.isActive('underline') ?? false,
        canUnderline: ctx.editor.can().chain().toggleUnderline().run() ?? false,
        canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
        isAlignLeft: ctx.editor.isActive({ textAlign: 'left' }) ?? false,
        isAlignCenter: ctx.editor.isActive({ textAlign: 'center' }) ?? false,
        isAlignRight: ctx.editor.isActive({ textAlign: 'right' }) ?? false,
        canSetTextAlign: ctx.editor.can().chain().setTextAlign('left').run() ?? false,
        isCode: ctx.editor.isActive('code') ?? false,
        canCode: ctx.editor.can().chain().toggleCode().run() ?? false,
        canClearMarks: ctx.editor.can().chain().unsetAllMarks().run() ?? false,
        isLink: ctx.editor.isActive('link'),
        canSetLink:
          ctx.editor.isEditable && ctx.editor.can().setLink({ href: 'https://example.com' }),
        isParagraph: ctx.editor.isActive('paragraph') ?? false,
        isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
        isBulletList: ctx.editor.isActive('bulletList') ?? false,
        isOrderedList: ctx.editor.isActive('orderedList') ?? false,
        isCodeBlock: ctx.editor.isActive('codeBlock') ?? false,
        isBlockquote: ctx.editor.isActive('blockquote') ?? false,
        canUndo: ctx.editor.can().chain().undo().run() ?? false,
        canRedo: ctx.editor.can().chain().redo().run() ?? false,
        canInsertPageBreak: ctx.editor.can().chain().insertPageBreak().run() ?? false,
        canInsertImagePlaceholder:
          ctx.editor.can().chain().insertImagePlaceholder('').run() ?? false,
        isInTable:
          ctx.editor.isActive('table') ||
          ctx.editor.isActive('tableCell') ||
          ctx.editor.isActive('tableHeader'),
        canAddRowBefore: ctx.editor.can().chain().addRowBefore().run() ?? false,
        canAddRowAfter: ctx.editor.can().chain().addRowAfter().run() ?? false,
        canAddColumnBefore: ctx.editor.can().chain().addColumnBefore().run() ?? false,
        canAddColumnAfter: ctx.editor.can().chain().addColumnAfter().run() ?? false,
        canDeleteRow: ctx.editor.can().chain().deleteRow().run() ?? false,
        canDeleteColumn: ctx.editor.can().chain().deleteColumn().run() ?? false
      };
    }
  });

  return (
    <div
      className={
        subtle
          ? 'border-t border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 py-1.5'
          : 'bg-neutral-100 dark:bg-neutral-700 rounded-t-md p-2'
      }
    >
      <div
        // A narrow column, such as the composer rail or a phone, runs out of toolbar
        // before it runs out of buttons, and the editor clips its own overflow.
        className="button-group flex flex-row flex-wrap items-center gap-0.5 gap-y-1"
      >
        {/* Text Color */}
        <ColorPickerButton
          text="A"
          color={editorState.currentTextColor}
          onChange={(color) => editor.chain().focus().setColor(color).run()}
          disabled={!editorState.canSetTextColor}
          title="Text Color"
        />
        {/* Highlight Color */}
        <ColorPickerButton
          icon={
            editorState.hasSelection ? (
              <PaintBrushIcon className="size-3" />
            ) : (
              <FillBucketIcon className="size-3" />
            )
          }
          color={editorState.currentHighlightColor}
          onChange={(color) => editor.chain().focus().setSmartBackgroundColor(color).run()}
          disabled={!editorState.canSetHighlight}
          title="Fill or Highlight"
        />
        {/* Clear Highlight */}
        <Headless.Button
          onClick={() => editor.chain().focus().clearSmartBackgroundColor().run()}
          disabled={!editorState.canSetHighlight}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
            editorState.canSetHighlight
              ? 'text-neutral-700 dark:text-neutral-300'
              : 'text-neutral-400 dark:text-neutral-500'
          )}
          title="Clear Fill or Highlight"
        >
          <NoSymbolIcon className="size-4" />
        </Headless.Button>
        {/* Font Size Selector */}
        <FontSizeSelector
          currentFontSize={editorState.currentFontSize}
          onSetFontSize={(size) => editor.chain().focus().setFontSize(size).run()}
          onUnsetFontSize={() => editor.chain().focus().unsetFontSize().run()}
          disabled={!editorState.canSetFontSize}
        />
        <Headless.Button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editorState.canBold}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
            editorState.isBold
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          <BoldIcon className="size-4" />
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editorState.canItalic}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
            editorState.isItalic
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          <ItalicIcon className="size-4" />
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editorState.canUnderline}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
            editorState.isUnderline
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          <UnderlineIcon className="size-4" />
        </Headless.Button>
        <EditorLinkButton
          editor={editor}
          active={editorState.isLink}
          disabled={!editorState.canSetLink}
        />
        <Headless.Button
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          disabled={!editorState.canSetTextAlign}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
            editorState.isAlignLeft
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          <Bars3BottomLeftIcon className="size-4" />
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          disabled={!editorState.canSetTextAlign}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
            editorState.isAlignCenter
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          <Bars3CenterIcon className="size-4" />
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          disabled={!editorState.canSetTextAlign}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
            editorState.isAlignRight
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          <Bars3BottomRightIcon className="size-4" />
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editorState.canStrike}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
            editorState.isStrike
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          <StrikethroughIcon className="size-4" />
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().toggleCode().run()}
          disabled={!editorState.canCode}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
            editorState.isCode
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          <CodeBracketIcon className="size-4" />
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().setParagraph().run()}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md text-sm',
            editorState.isParagraph
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          P
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md text-sm',
            editorState.isHeading1
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          H1
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md text-sm',
            editorState.isHeading2
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          H2
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md text-sm',
            editorState.isHeading3
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          H3
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
            editorState.isBulletList
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          <ListBulletIcon className="size-4" />
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
            editorState.isOrderedList
              ? 'text-primary-500 dark:text-primary-400'
              : 'text-neutral-500 dark:text-neutral-400'
          )}
        >
          <NumberedListIcon className="size-4" />
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().insertPageBreak().run()}
          disabled={!editorState.canInsertPageBreak}
          className={clsx(
            'px-2 py-1 text-xs rounded-md whitespace-nowrap',
            editorState.canInsertPageBreak
              ? 'hover:bg-neutral-200 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300'
              : 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
          )}
          title="Insert page break"
        >
          Page break
        </Headless.Button>
        {showImageButton && (
          <Headless.Button
            onClick={() => {
              // Insert an image placeholder directly without asking for an identifier/alt text
              editor.chain().focus().insertImagePlaceholder('').run();
            }}
            disabled={!editorState.canInsertImagePlaceholder}
            className={clsx(
              'px-2 py-1 text-xs rounded-md whitespace-nowrap flex items-center gap-1',
              editorState.canInsertImagePlaceholder
                ? 'hover:bg-neutral-200 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300'
                : 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
            )}
            title="Insert image placeholder"
          >
            <PhotoIcon className="size-4" />
            <span>Image</span>
          </Headless.Button>
        )}
        <Headless.Button
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editorState.canUndo}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md text-neutral-500 dark:text-neutral-400'
          )}
        >
          <ArrowUturnLeftIcon className="size-4" />
        </Headless.Button>
        <Headless.Button
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editorState.canRedo}
          className={clsx(
            'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md text-neutral-500 dark:text-neutral-400'
          )}
        >
          <ArrowUturnRightIcon className="size-4" />
        </Headless.Button>
        {onPreviewToggle && (
          <Headless.Button
            onClick={onPreviewToggle}
            className={clsx(
              'p-1 hover:bg-neutral-200 dark:hover:bg-white/10 rounded-md',
              showPreview
                ? 'text-primary-500 dark:text-primary-400'
                : 'text-neutral-500 dark:text-neutral-400'
            )}
            title="Preview"
          >
            <EyeIcon className="size-4" />
          </Headless.Button>
        )}
      </div>
      {/* Table Controls - Second Row */}
      {editorState.isInTable && (
        <div className="button-group flex flex-row items-center gap-1 mt-2 pt-2 border-t border-neutral-200 dark:border-neutral-700">
          <Headless.Button
            onClick={() => editor.chain().focus().addRowBefore().run()}
            disabled={!editorState.canAddRowBefore}
            className={clsx(
              'px-2 py-1 text-xs rounded-md',
              editorState.canAddRowBefore
                ? 'hover:bg-neutral-200 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300'
                : 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
            )}
          >
            Insert row before
          </Headless.Button>
          <Headless.Button
            onClick={() => editor.chain().focus().addRowAfter().run()}
            disabled={!editorState.canAddRowAfter}
            className={clsx(
              'px-2 py-1 text-xs rounded-md',
              editorState.canAddRowAfter
                ? 'hover:bg-neutral-200 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300'
                : 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
            )}
          >
            Insert row after
          </Headless.Button>
          <Headless.Button
            onClick={() => editor.chain().focus().addColumnBefore().run()}
            disabled={!editorState.canAddColumnBefore}
            className={clsx(
              'px-2 py-1 text-xs rounded-md',
              editorState.canAddColumnBefore
                ? 'hover:bg-neutral-200 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300'
                : 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
            )}
          >
            Insert col before
          </Headless.Button>
          <Headless.Button
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            disabled={!editorState.canAddColumnAfter}
            className={clsx(
              'px-2 py-1 text-xs rounded-md',
              editorState.canAddColumnAfter
                ? 'hover:bg-neutral-200 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300'
                : 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
            )}
          >
            Insert col after
          </Headless.Button>
          <Headless.Button
            onClick={() => editor.chain().focus().deleteRow().run()}
            disabled={!editorState.canDeleteRow}
            className={clsx(
              'px-2 py-1 text-xs rounded-md',
              editorState.canDeleteRow
                ? 'hover:bg-neutral-200 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300'
                : 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
            )}
          >
            Delete row
          </Headless.Button>
          <Headless.Button
            onClick={() => editor.chain().focus().deleteColumn().run()}
            disabled={!editorState.canDeleteColumn}
            className={clsx(
              'px-2 py-1 text-xs rounded-md',
              editorState.canDeleteColumn
                ? 'hover:bg-neutral-200 dark:hover:bg-white/10 text-neutral-700 dark:text-neutral-300'
                : 'text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
            )}
          >
            Delete col
          </Headless.Button>
        </div>
      )}
    </div>
  );
};
